import { useEffect, useMemo, useState } from "react";
import { TodoStatus } from "@acto/gql/graphql";
import type { Todo } from "@acto/taskpane/core/domain";
import type { useUpdateTodos } from "@acto/taskpane/core/data-access/todos/useUpdateTodos";
import { AnalyticsEvents, getTodoSource, useTrack } from "@acto/taskpane/core/analytics";
import { useUndoBatch } from "@acto/taskpane/field-sales-2/utils/hooks/useUndoBatch";
import { dropIds } from "@acto/taskpane/field-sales-2/utils/collections";

type UpdateTodosFn = ReturnType<typeof useUpdateTodos>["updateTodos"];
type TrackFn = ReturnType<typeof useTrack>;
type TodoStatusEvent = typeof AnalyticsEvents.TODO_COMPLETED | typeof AnalyticsEvents.TODO_REOPENED;

function trackBatch(track: TrackFn, event: TodoStatusEvent, batch: ReadonlyMap<string, Todo>) {
  batch.forEach((todo, todoId) => {
    track(event, {
      source: getTodoSource(todo),
      todoId,
      customerId: todo.client?.id,
      customerName: todo.client?.name,
    });
  });
}

interface Buckets {
  overdue: Todo[];
  open: Todo[];
  completed: Todo[];
}

export interface OptimisticTodoCompletion {
  /** The three buckets with the optimistic-completion overlay applied. */
  overdueShown: Todo[];
  openShown: Todo[];
  completedShown: Todo[];
  isCompleting: (id: string) => boolean;
  handleComplete: (todo: Todo) => void;
  handleUndo: () => void;
  pendingCount: number;
  /** Whether a completed todo's revert-to-open is currently pending/committing. */
  isReverting: (id: string) => boolean;
  /** Reverts a completed todo back to Open — the mirror image of `handleComplete`. */
  handleUncomplete: (todo: Todo) => void;
  handleUndoRevert: () => void;
  revertPendingCount: number;
  /** Commit any open grace batch immediately, surrendering its undo window. */
  flushPending: () => void;
}

/**
 * Optimistic complete + batch-undo for the global Todos page. Tapping a todo checks it in place and
 * opens the shared grace window; while it runs the undo banner reverts the whole batch with no
 * server call. When the window elapses the batch is optimistically relocated to Completed and the
 * COMPLETED mutations fire; a failed mutation rolls that todo back into its bucket. Optimistic
 * entries drop once the server refetch has moved them out of the active (overdue/open) buckets.
 *
 * Reverting a completed todo (`handleUncomplete`) is the mirror image: it opens its own window and,
 * once that elapses, optimistically relocates the todo to Open and fires an OPEN mutation. A
 * reverted todo always resurfaces in Open regardless of its original due date — recomputing whether
 * it's still overdue is the server's job on the next refetch, not this optimistic overlay's.
 *
 * Returns the three buckets with the optimistic overlay already applied (`*Shown`).
 */
export function useOptimisticTodoCompletion(
  updateTodos: UpdateTodosFn,
  { overdue, open, completed }: Buckets,
): OptimisticTodoCompletion {
  const [optimisticCompleted, setOptimisticCompleted] = useState<Map<string, Todo>>(() => new Map());
  const [optimisticReopened, setOptimisticReopened] = useState<Map<string, Todo>>(() => new Map());
  const track = useTrack();

  // Normal commit path: `useUpdateTodos` swallows mutation errors and resolves to null, so on failure
  // roll the todo back out of the optimistic overlay — it returns to its bucket instead of lingering
  // as a "completed" ghost that would reappear on the next load.
  const completeTodo = async (todoId: string) => {
    const updated = await updateTodos({ input: { id: todoId, processedStatus: TodoStatus.Completed } });
    if (updated) return;
    setOptimisticCompleted((prev) => dropIds(prev, [todoId]));
  };

  const completions = useUndoBatch<Todo>((batch) => {
    setOptimisticCompleted((prev) => {
      const next = new Map(prev);
      batch.forEach((todo, todoId) => next.set(todoId, todo));
      return next;
    });
    // The two overlays are mutually exclusive: a todo is optimistically completed or optimistically
    // reopened, never both. Leaving a stale entry in the opposite map filters the row out of the
    // bucket it belongs to AND the one it came from, so it renders nowhere until the page remounts.
    setOptimisticReopened((prev) => dropIds(prev, batch.keys()));
    trackBatch(track, AnalyticsEvents.TODO_COMPLETED, batch);
    batch.forEach((_todo, todoId) => void completeTodo(todoId));
  });

  // Mirror of `completeTodo`: on mutation failure, drop the todo back out of the optimistic overlay
  // so it re-shows as completed instead of lingering as a ghost in Open.
  const uncompleteTodo = async (todoId: string) => {
    const updated = await updateTodos({ input: { id: todoId, processedStatus: TodoStatus.Open } });
    if (updated) return;
    setOptimisticReopened((prev) => dropIds(prev, [todoId]));
  };

  const reverts = useUndoBatch<Todo>((batch) => {
    setOptimisticReopened((prev) => {
      const next = new Map(prev);
      batch.forEach((todo, todoId) => next.set(todoId, todo));
      return next;
    });
    // Mirror of the completion commit's overlay exclusion above.
    setOptimisticCompleted((prev) => dropIds(prev, batch.keys()));
    trackBatch(track, AnalyticsEvents.TODO_REOPENED, batch);
    batch.forEach((_todo, todoId) => void uncompleteTodo(todoId));
  });

  const { schedule: scheduleCompletion, flush: flushCompletions } = completions;
  const { schedule: scheduleRevert, flush: flushReverts } = reverts;

  const flushPending = () => {
    flushCompletions();
    flushReverts();
  };

  const handleComplete = (todo: Todo) => {
    flushReverts();
    scheduleCompletion(todo.id, todo);
  };

  const handleUncomplete = (todo: Todo) => {
    flushCompletions();
    scheduleRevert(todo.id, todo);
  };

  // Drop optimistic entries once the server's refetch has moved them out of the active buckets.
  useEffect(() => {
    setOptimisticCompleted((prev) => {
      if (prev.size === 0) return prev;
      const activeIds = new Set<string>([...overdue, ...open].map((todo) => todo.id));
      let changed = false;
      const next = new Map(prev);
      prev.forEach((_todo, todoId) => {
        if (!activeIds.has(todoId)) {
          next.delete(todoId);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [overdue, open]);

  // Mirror: drop optimistically-reopened entries once the server's refetch has moved them out of
  // the completed bucket.
  useEffect(() => {
    setOptimisticReopened((prev) => {
      if (prev.size === 0) return prev;
      const completedIds = new Set(completed.map((todo) => todo.id));
      let changed = false;
      const next = new Map(prev);
      prev.forEach((_todo, todoId) => {
        if (!completedIds.has(todoId)) {
          next.delete(todoId);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [completed]);

  const { overdueShown, openShown, completedShown } = useMemo(() => {
    const completedServerIds = new Set(completed.map((todo) => todo.id));
    const overlayCompleted = [...optimisticCompleted.values()].filter((todo) => !completedServerIds.has(todo.id));
    const activeServerIds = new Set<string>([...overdue, ...open].map((todo) => todo.id));
    const overlayReopened = [...optimisticReopened.values()].filter((todo) => !activeServerIds.has(todo.id));
    return {
      overdueShown: overdue.filter((todo) => !optimisticCompleted.has(todo.id)),
      openShown: [...open.filter((todo) => !optimisticCompleted.has(todo.id)), ...overlayReopened],
      completedShown: [...overlayCompleted, ...completed].filter((todo) => !optimisticReopened.has(todo.id)),
    };
  }, [overdue, open, completed, optimisticCompleted, optimisticReopened]);

  const isCompleting = (id: string) => completions.pending.has(id);
  const isReverting = (id: string) => reverts.pending.has(id);

  return {
    overdueShown,
    openShown,
    completedShown,
    isCompleting,
    handleComplete,
    handleUndo: completions.undo,
    pendingCount: completions.pending.size,
    isReverting,
    handleUncomplete,
    handleUndoRevert: reverts.undo,
    revertPendingCount: reverts.pending.size,
    flushPending,
  };
}
